/**
 * IX3 Data Filtering Utilities
 *
 * Shared utilities for filtering interactions and timelines by page.
 * Used by both getIx3Js (publishing) and getIx3Stylesheet (FOUC prevention).
 */

import type {
  Interaction,
  Timeline,
  IX3InteractionsMap,
} from '@packages/systems/ix3/core';
import {weakMemo} from '@packages/utilities/memo';

/**
 * Gets interactions for a specific page.
 * Returns both site-wide and page-specific interactions.
 *
 * @param ix3InteractionsMap - Map of interactions by page ID
 * @param pageId - Page ID to get interactions for
 * @returns Array of interactions (site-wide + page-specific)
 *
 * @example
 * const interactions = getInteractionsForPage(ix3InteractionsMap, 'page-123');
 * // Returns: [...siteInteractions, ...page123Interactions]
 */
export function getInteractionsForPage(
  ix3InteractionsMap: IX3InteractionsMap,
  pageId: string
): Interaction[] {
  const pageInteractions = ix3InteractionsMap[pageId] || [];
  const siteInteractions = ix3InteractionsMap.site || [];
  return siteInteractions.concat(pageInteractions);
}

/**
 * Creates a map of timelines by ID for fast lookup.
 * Memoized for performance.
 *
 * @param timelines - Array of all timelines
 * @returns Map of timeline ID to timeline object
 */
export const toTimelineMap = weakMemo((timelines: Timeline[]) =>
  timelines.reduce<Map<Timeline['id'], Timeline>>((acc, timeline) => {
    acc.set(timeline.id, timeline);
    return acc;
  }, new Map<Timeline['id'], Timeline>())
);

/**
 * Filters timelines to only those referenced by the given interactions.
 * This reduces the data set for transformation and generation.
 *
 * @param interactions - Interactions to get timelines for
 * @param allTimelines - All available timelines
 * @returns Array of timelines used by the interactions
 *
 * @example
 * const timelines = getTimelinesForInteractions(pageInteractions, allTimelines);
 * // Returns only timelines referenced in pageInteractions.timelineIds
 */
export function getTimelinesForInteractions(
  interactions: Interaction[],
  allTimelines: Timeline[]
): Timeline[] {
  const timelineMap = toTimelineMap(allTimelines);
  const timelines: Timeline[] = [];
  const addedIds = new Set<string>();

  for (const interaction of interactions) {
    for (const timelineId of interaction.timelineIds) {
      if (addedIds.has(timelineId)) continue; // Skip duplicates

      const timeline = timelineMap.get(timelineId);
      if (timeline) {
        timelines.push(timeline);
        addedIds.add(timelineId);
      }
    }
  }

  return timelines;
}
